/**
 * Digital Signal Processing Pipeline for Coasting Detection
 *
 * Simplified pipeline that uses pre-processed telemetry data from backend.
 * Processing now happens in the backend, so this pipeline focuses on
 * anomaly removal and visual smoothing only.
 */

import { type Lap } from '../../domain/lap/Lap'
import { collectAndClassifyCoastingData } from './dataCollection'
import {
  removeAnomalies,
  fillCoastingGaps,
  removeIsolatedSpikes,
} from './anomalyRemoval'
import {
  applyFinalIntensitySmoothing,
  applyPostSmoothingCleanup,
} from './visualSmoothing'
import { applyTransitionInfluence } from './transitionDetection'
import { type CoastingPeriod, type CoastingConfig } from './types'
import type { VehicleStrategy } from './vehicles/VehicleStrategy'

export const executeCoastingPipeline = (
  selectedLaps: Lap[],
  config: CoastingConfig,
  vehicleStrategy: VehicleStrategy
): CoastingPeriod[] => {
  // Use pre-processed telemetry from backend to classify coasting
  const classifiedData = [
    ...collectAndClassifyCoastingData(selectedLaps, config, vehicleStrategy),
  ]

  // Remove noise while preserving signal
  const cleanedData = signalCleaning(classifiedData)

  // Apply human factors & visual polish
  const transitionAdjustedData = applyTransitionInfluence(
    cleanedData,
    vehicleStrategy
  )
  const finalData = applyVisualSmoothing(transitionAdjustedData)

  return finalData
}

const signalCleaning = (classifiedData: CoastingPeriod[]): CoastingPeriod[] => {
  const cleanedData = removeAnomalies(classifiedData)
  const gapFilledData = fillCoastingGaps(cleanedData)
  return removeIsolatedSpikes(gapFilledData)
}

const applyVisualSmoothing = (
  transitionAdjustedData: CoastingPeriod[]
): CoastingPeriod[] => {
  const smoothedIntensityData = applyFinalIntensitySmoothing(
    transitionAdjustedData
  )
  return applyPostSmoothingCleanup(smoothedIntensityData)
}
